import React, {
  createContext,
  useCallback,
  useContext,
  useState,
  useEffect,
} from "react";
import { PatchMedicalHistoryVaccinations } from "../../basic-info/calculateBasicInfoCompletion";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { VACCINE_TYPE_VALUES } from "./options";
import { hydrateMedications } from "./medicationState";

export type MedicationProductType =
  | "nose_drops"
  | "cold_medicine"
  | "diuretics"
  | "pain_relief"
  | "relaxants"
  | "sleeping_pills"
  | "allergy_medication"
  | "nicotine_subs"
  | "other"
  | "none";

export type VaccineType = PatchMedicalHistoryVaccinations[number]["type"];

export interface MedicationDetail {
  id?: string;
  category?: MedicationProductType | null;
  name?: string | null;
  brand?: string | null;
  frequency?: string | null;
  notes?: string | null;
}

export interface VaccineDetail {
  id?: string;
  vaccine: VaccineType | null;
  date?: Date | null;
  brand?: string | null;
  notes?: string | null;
}

export interface MedicationsVaccinesState {
  currentlyTakingMedications?: boolean | null;
  selectedProducts: MedicationProductType[];
  medicationDetails: MedicationDetail[];
  medicationIdsToDelete: string[];
  otherMedicationText: string;
  selectedVaccines: VaccineType[] | ["none"];
  vaccineDetails: VaccineDetail[];
  vaccineIdsToDelete: string[];
  otherVaccineText: string;
}

const initialState: MedicationsVaccinesState = {
  currentlyTakingMedications: null,
  selectedProducts: [],
  medicationDetails: [],
  medicationIdsToDelete: [],
  otherMedicationText: "",
  selectedVaccines: [],
  vaccineDetails: [],
  vaccineIdsToDelete: [],
  otherVaccineText: "",
};

interface MedicationsVaccinesContextValue {
  state: MedicationsVaccinesState;
  updateState: (updates: Partial<MedicationsVaccinesState>) => void;
  resetState: () => void;
}

const MedicationsVaccinesContext =
  createContext<MedicationsVaccinesContextValue | null>(null);

export function MedicationsVaccinesProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: patient, isLoading } = usePatient();
  const [state, setState] = useState<MedicationsVaccinesState>(initialState);

  // Load data from patient (like InfertilityContext)
  useEffect(() => {
    // Wait for patient data to load
    if (isLoading) {
      return;
    }

    if (!patient) {
      return;
    }

    setState((prev) => {
      // Check if state is in initial empty state - if so, always try to load
      const isInitialState =
        prev.currentlyTakingMedications === null &&
        prev.medicationDetails.length === 0 &&
        prev.vaccineDetails.length === 0;

      const next: Partial<MedicationsVaccinesState> = {};

      const meds = patient.medicalHistory?.medications;
      if (Array.isArray(meds) && meds.length > 0) {
        // Load medications ONLY if state is truly empty (initial state)
        if (isInitialState || prev.medicationDetails.length === 0) {
          const hydratedMedications = hydrateMedications(meds);
          next.selectedProducts = hydratedMedications.selectedProducts;
          next.medicationDetails = hydratedMedications.medicationDetails;
        }

        // Load currentlyTakingMedications (only if empty or initial state)
        if (
          (isInitialState || prev.currentlyTakingMedications === null) &&
          meds.length > 0
        ) {
          next.currentlyTakingMedications = true;
        }
      } else if (
        (isInitialState || prev.currentlyTakingMedications === null) &&
        patient.medicalHistory?.currentlyTakingMedications === false
      ) {
        next.selectedProducts = ["none"];
        next.currentlyTakingMedications = false;
      }

      const vaccinations = patient.medicalHistory?.vaccinations;
      if (Array.isArray(vaccinations) && vaccinations.length > 0) {
        // Load vaccinations ONLY if state is truly empty (initial state)
        if (isInitialState || prev.vaccineDetails.length === 0) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const details = vaccinations.map((v: any) => {
            const typeValue =
              VACCINE_TYPE_VALUES.find((value) => value === v?.type) ?? "other";
            const noteValue =
              (typeof v?.notes === "string" && v.notes.length > 0
                ? v.notes
                : typeof v?.other === "string"
                  ? v.other
                  : "") || null;
            return {
              id: v?.id ?? undefined,
              vaccine: typeValue as VaccineType,
              date: v?.dateReceived ? new Date(v.dateReceived) : null,
              brand: v?.brand ?? null,
              notes: noteValue,
            };
          });

          const selectedVaccines = Array.from(
            new Set(details.map((detail) => detail.vaccine ?? "other")),
          ) as VaccineType[];

          next.selectedVaccines = selectedVaccines;
          next.vaccineDetails = details;
        }
      }

      // Load currentlyTakingMedications from patient if not set yet
      if (
        (isInitialState || prev.currentlyTakingMedications === null) &&
        patient.medicalHistory?.currentlyTakingMedications !== undefined &&
        next.currentlyTakingMedications === undefined
      ) {
        next.currentlyTakingMedications =
          patient.medicalHistory.currentlyTakingMedications;
      }

      // Only update if there are changes
      if (Object.keys(next).length === 0) {
        return prev;
      }

      // Merge with previous state (don't replace)
      return { ...prev, ...next };
    });
  }, [patient, isLoading]);

  const updateState = useCallback(
    (updates: Partial<MedicationsVaccinesState>) => {
      setState((prev) => ({ ...prev, ...updates }));
    },
    [],
  );

  const resetState = useCallback(() => setState(initialState), []);

  return (
    <MedicationsVaccinesContext.Provider
      value={{ state, updateState, resetState }}
    >
      {children}
    </MedicationsVaccinesContext.Provider>
  );
}

export function useMedicationsVaccines() {
  const ctx = useContext(MedicationsVaccinesContext);
  if (!ctx) {
    throw new Error(
      "useMedicationsVaccines must be used within MedicationsVaccinesProvider",
    );
  }
  return ctx;
}
